import { useEffect, useCallback, useRef } from "react";
import { useGroup } from "@/lib/context/GroupContext";
import {
  savePushSubscription,
  removePushSubscriptionsForGroup,
} from "@/lib/api/pushNotifications";
import { getPushRegistration, isPushSupported } from "@/lib/push/getPushRegistration";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function subscribePushForGroup(groupId: string): Promise<boolean> {
  if (!VAPID_PUBLIC_KEY || !isPushSupported()) return false;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;

  const registration = await getPushRegistration();
  if (!registration) return false;

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    }));

  await savePushSubscription(groupId, subscription.toJSON() as PushSubscriptionJSON);
  return true;
}

async function unsubscribePushForGroup(groupId: string): Promise<void> {
  const registration = await getPushRegistration();
  if (registration) {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  }
  await removePushSubscriptionsForGroup(groupId);
}

export function usePushNotifications(enabled = true) {
  const { currentGroup } = useGroup();
  const prevEnabled = useRef<boolean | null>(null);

  const subscribe = useCallback(async () => {
    if (!enabled || !currentGroup?.id) return;
    try {
      await subscribePushForGroup(currentGroup.id);
    } catch (err) {
      console.warn("Push subscription failed:", err);
    }
  }, [currentGroup?.id, enabled]);

  const unsubscribe = useCallback(async () => {
    if (!currentGroup?.id) return;
    try {
      await unsubscribePushForGroup(currentGroup.id);
    } catch (err) {
      console.warn("Push unsubscribe failed:", err);
    }
  }, [currentGroup?.id]);

  useEffect(() => {
    if (prevEnabled.current === true && !enabled) {
      unsubscribe();
    } else if (enabled) {
      subscribe();
    }
    prevEnabled.current = enabled;
  }, [enabled, subscribe, unsubscribe]);

  return { subscribe, unsubscribe };
}

/** Imperative subscribe for profile settings toggle. */
export async function subscribeToPush(groupId: string): Promise<boolean> {
  try {
    return await subscribePushForGroup(groupId);
  } catch (err) {
    console.warn("subscribeToPush failed:", err);
    return false;
  }
}

/** Imperative unsubscribe for profile settings toggle. */
export async function unsubscribeFromPush(groupId: string): Promise<void> {
  try {
    await unsubscribePushForGroup(groupId);
  } catch (err) {
    console.warn("unsubscribeFromPush failed:", err);
  }
}
